import { css } from '@emotion/react'
import { useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Size, Spacing, ThemedColors, Typography, gemVar } from '@opallabs/gem'
import { ThemeProvider, useTheme, useWorkspace } from '@opallabs/hooks'
import {
  Avatar,
  Button,
  ButtonIconPosition,
  ButtonSize,
  DotLoader,
  Icon,
  Link,
  Popover,
} from '@opallabs/lapidary'
import { OpalRoutes } from '@opallabs/routing'
import { useMomentBoards, MomentBoard } from './useMomentBoards'

const styles = {
  content: css({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    // Radix measures the room left below the trigger; without this cap a
    // moment on many boards pushes the list off the bottom of the screen.
    maxHeight: 'var(--radix-popover-content-available-height)',
    padding: '16px 12px 12px',
    width: 300,
  }),
  heading: css({
    color: gemVar(ThemedColors.TextPrimary),
    fontSize: 14,
    fontWeight: 700,
    flex: 'none',
    margin: '0 0 13px',
  }),
  list: css({
    listStyle: 'none',
    margin: 0,
    minHeight: 0,
    overflowY: 'auto',
    padding: 0,
  }),
  row: css({
    alignItems: 'center',
    color: gemVar(ThemedColors.TextPrimary),
    display: 'flex',
    gap: 4,
    height: 32,
    padding: '0 8px',
  }),
  title: css({
    flex: '1 1 auto',
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  // Mirrors the CustomFieldsSelectList "Not seeing what you need?" panel.
  empty: css({
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface2),
    borderRadius: Size.Rounding,
    color: gemVar(ThemedColors.TextSecondary),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: Spacing.M,

    p: {
      ...Typography.T500,
      margin: 0,
      marginBlockEnd: Spacing.XS,
      textAlign: 'center',
    },
  }),
}

const BoardRow: React.FC<{ board: MomentBoard; href: string }> = ({
  board,
  href,
}) => (
  <li>
    <Link css={styles.row} href={href}>
      <Icon ariaHidden glyph="gem-grid" useHypernova={false} />
      <span css={styles.title}>{board.title}</span>
      {board.ownerName && (
        <Avatar
          fullName={board.ownerName}
          size={16}
          src={board.ownerAvatarUrl}
          tooltipped
        />
      )}
      <Icon
        ariaLabel={board.isPublic ? 'Public board' : 'Private board'}
        glyph={board.isPublic ? 'gem-globe' : 'gem-locked'}
        useHypernova={false}
      />
    </Link>
  </li>
)

export type MomentBoardsPopoverProps = Readonly<{
  momentUuid: string
  /**
   * Adds an "Add to Board" button to the empty state. The popover closes
   * before this is called so it doesn't cover the Add to Board UI. Omit for
   * view-only users.
   */
  onAddToBoard?: () => void
  zIndex?: number
}>

/**
 * Lives inside Popover.Content, which Radix mounts only while the popover is
 * open. That keeps useMomentBoards — two requests — off the moment shroud's
 * render path, so opening a moment costs nothing until someone asks for its
 * boards.
 */
const BoardsList: React.FC<{
  momentUuid: string
  onAddToBoard?: () => void
}> = ({ momentUuid, onAddToBoard }) => {
  const { slug: workspaceSlug } = useWorkspace()
  const { boards, isLoading } = useMomentBoards(momentUuid)

  if (isLoading) return <DotLoader height={32} />

  if (boards.length === 0) {
    return (
      <div css={styles.empty}>
        <p>This moment isn&rsquo;t on any boards yet.</p>
        {onAddToBoard && (
          <Button
            buttonText="Add to Board"
            icon={<Icon ariaHidden glyph="gem-add" useHypernova={false} />}
            iconPosition={ButtonIconPosition.leading}
            name="add-to-board"
            onClick={onAddToBoard}
            size={ButtonSize.Small}
            variant="tertiary"
          />
        )}
      </div>
    )
  }

  return (
    <ul css={styles.list}>
      {boards.map((board) => (
        <BoardRow
          board={board}
          href={generatePath(OpalRoutes.Board, {
            boardId: board.id,
            workspaceSlug,
          })}
          key={board.id}
        />
      ))}
    </ul>
  )
}

/**
 * A sidebar icon button that lists the boards a moment belongs to: each
 * board's title, owner, and privacy state.
 *
 * The trigger stays on the surrounding (dark) shroud theme, but the light
 * ThemeProvider must wrap Popover.Content itself: the content portals to
 * document.body and the portal re-scopes Gem's CSS variables from the theme
 * at its render site, so a provider inside the content is too late to make
 * the popover surface light.
 */
export const MomentBoardsPopover: React.FC<MomentBoardsPopoverProps> = ({
  momentUuid,
  onAddToBoard,
  zIndex,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          ariaLabel="Boards"
          ariaPressed={open}
          buttonText="Boards"
          hideButtonText
          icon={<Icon ariaHidden glyph="gem-grid" useHypernova={false} />}
          iconPosition={ButtonIconPosition.leading}
          name="moment-boards"
          theme={theme}
          variant="ghost"
        />
      </Popover.Trigger>
      <ThemeProvider theme="light">
        <Popover.Content
          align="start"
          collisionPadding={8}
          // Radix skips links when it picks what to focus on open, so the
          // panel itself takes focus and Chrome draws its default ring on it.
          css={{ outline: 'none' }}
          sideOffset={4}
          zIndex={zIndex}
        >
          <div css={styles.content}>
            <h3 css={styles.heading}>Boards</h3>
            <BoardsList
              momentUuid={momentUuid}
              onAddToBoard={
                onAddToBoard &&
                (() => {
                  setOpen(false)
                  onAddToBoard()
                })
              }
            />
          </div>
        </Popover.Content>
      </ThemeProvider>
    </Popover.Root>
  )
}
